import { useState } from 'react';
import { useMarketRealtime } from '@/lib/realtime';
import type { LivePrice } from '@/lib/binary-market';
import type { BuyTargetDto, MarketOptionDto } from '@/lib/models';

/**
 * Live prices (cents) keyed by **external** (Plaee) outcome id — that's the id
 * the Centrifugo `price_updated` event carries (`toPriceUpdated` passes it
 * through untranslated), and the id positions/buy-targets resolve against.
 */
export type OutcomePriceMap = Record<string, LivePrice | undefined>;

/** Holds the live-price overlay for a set of outcomes on one surface. */
export function useOutcomePrices(): {
  prices: OutcomePriceMap;
  setPrice: (outcomeId: string, price: LivePrice) => void;
  seedPrice: (outcomeId: string, price: LivePrice) => void;
} {
  const [prices, setPrices] = useState<OutcomePriceMap>({});
  function setPrice(outcomeId: string, price: LivePrice) {
    setPrices((prev) => {
      const cur = prev[outcomeId];
      if (cur && cur.yesCents === price.yesCents && cur.noCents === price.noCents) return prev;
      return { ...prev, [outcomeId]: price };
    });
  }
  // Fill-if-absent variant for REST snapshots (e.g. the order-book seed): a
  // snapshot must never regress a Centrifugo tick that already arrived.
  function seedPrice(outcomeId: string, price: LivePrice) {
    setPrices((prev) => (prev[outcomeId] ? prev : { ...prev, [outcomeId]: price }));
  }
  return { prices, setPrice, seedPrice };
}

/**
 * Headless subscriber for one market channel — forwards every outcome's live
 * price (cents) keyed by its **external** (Plaee) outcome id, the id the
 * `price_updated` event carries. Render one per distinct market.
 */
export function MarketOutcomePricesSubscriber({
  marketId,
  externalMarketId,
  onPrice,
}: {
  marketId: string;
  externalMarketId: string;
  onPrice: (outcomeId: string, price: LivePrice) => void;
}) {
  useMarketRealtime(marketId, externalMarketId, {
    onPriceUpdated: (e) => onPrice(e.outcomeId, { yesCents: e.yesPrice, noCents: e.noPrice }),
  });
  return null;
}

/**
 * Live-or-seed cents for one buy target, keyed by its **external** outcome id +
 * side. The map is filled from Centrifugo, whose `price_updated` carries the
 * external id — so we must read by `externalOutcomeId`, not the local cuid, or
 * the lookup never hits and we'd be stuck on the seed price forever.
 */
export function targetCents(target: BuyTargetDto, prices: OutcomePriceMap): number | null {
  const live = prices[target.externalOutcomeId];
  if (!live) return target.priceCents;
  return target.side === 'yes' ? live.yesCents : live.noCents;
}

/**
 * Live-or-seed Yes/No cents for an option. Each leg keys live by its own
 * outcome + side, so a 2-outcome binary (distinct Yes/No outcomes) ticks
 * correctly on both legs.
 */
export function optionCents(option: MarketOptionDto, prices: OutcomePriceMap): { yes: number | null; no: number | null } {
  return { yes: targetCents(option.yes, prices), no: targetCents(option.no, prices) };
}
